import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '../atoms/Tooltip';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import TFVLogoIcon from '../icons/TFVLogoIcon';
import SettingsGearIcon from '../icons/SettingsGearIcon';
import EyeIcon from '../icons/EyeIcon';
import NamedBox from '../atoms/NamedBox';
import ProgressLine from '../atoms/ProgressLine';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import {
  COLOR_BACKGROUND_WARM,
  COLOR_BORDER,
  COLOR_MIDDLE_GRAY,
  COLOR_ORANGE,
  COLOR_GREEN,
  COLOR_TEXT,
  COLOR_WHITE,
} from '../styles/colors';
import { PlayerValuationProfileFactors, Valuations, PlayerFactors } from '../types/PlayerValuationProfileFactors';
import TFPillLogoColorIcon from '../icons/TFPillLogoColorIcon';
import Team from '../types/Team';
import { formatNumber } from '../services/converter';
interface PlayerValuationsProps {
  className?: string;
  data?: PlayerValuationProfileFactors | null;
  evaluationTeam?: Team | null;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    border: `1px solid ${COLOR_BACKGROUND_WARM}`,
  },
  headerBar: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '64px',
    padding: theme.spacing(1, 2),
    fontFamily: FONT_PROXIMA_NOVA,
    color: COLOR_TEXT,
    background: COLOR_BACKGROUND_WARM,
  },
  headerName: {
    ...theme.typography.h2,
    fontWeight: 700,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    fontWeight: 700,
  },
  tfvIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
      
    '& svg': {
      width: '100%',
      height: 'auto',
    },
  },
  tfvIconHeader: {
    width: '48px',
  },
  headerRight: {
    minWidth: 100,
  },
  select: {
    fontSize: theme.typography.pxToRem(12),
  },
  summaryRow: {
    display: 'flex',
    flexWrap: 'nowrap',
    borderBottom: `1px solid ${COLOR_BORDER}`,
    background: COLOR_WHITE,
    minWidth: 'max-content',
  },
  summaryScroller: {
    width: '100%',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  summaryCell: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(3),
    borderRight: `1px solid ${COLOR_BORDER}`,

    '&:last-child': {
      borderRight: 0,
    },
  },
  summaryBlock: {
    flexGrow: 1,
  },
  summaryIconsCell: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  summaryIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
      
    '& svg': {
      width: '100%',
      height: 'auto',
    },
  },
  summaryValueCell: {
    flex: '0 0 180px',
    minWidth: 120,
    textAlign: 'right',
  },
  summaryInfoCell: {
    flex: '0 0 280px',
  },
  summaryScoreCell: {
    flex: '0 0 120px',
    justifyContent: 'center',
  },
  summaryTitle: {
    fontSize: theme.typography.pxToRem(24),
    lineHeight: 1.1,
    fontWeight: 700,
  },
  summarySubtitle: {
    fontSize: theme.typography.pxToRem(16),
  },
  summaryValueRow: {
    display: 'flex',
    flexDirection: 'column',
  },
  summaryValue: {
    fontSize: theme.typography.pxToRem(24),
    fontWeight: 700,
    color: COLOR_GREEN,
  },
  summaryScore: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '0 auto',
    fontSize: theme.typography.pxToRem(24),
  },
  miniScore: {
    fontWeight: 700,
  },
  miniLogo: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    background: COLOR_ORANGE,
  },
  factorsRow: {
    // padding: theme.spacing(1, 2, 2),
  },
  factorGrid: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  factorCol: {
    [theme.breakpoints.up('md')]: {
      borderRight: `1px solid ${COLOR_BORDER}`,
      '&:last-child': {
        borderRight: 'none',
      },
    },
    [theme.breakpoints.down('sm')]: {
      borderRight: 'none',
      borderBottom: `1px solid ${COLOR_BORDER}`,
      '&:last-child': {
        borderBottom: 'none',
      },
      paddingBottom: theme.spacing(1.5),
      marginBottom: theme.spacing(1.5),
    },
    paddingRight: 0,
  },
  factorRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1.25),
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  factorRowOdd: {
    backgroundColor: COLOR_WHITE,
  },
  factorRowEven: {
    backgroundColor: COLOR_BACKGROUND_WARM,
  },
  factorName: {
    width: 90,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
  },
  factorInfoIcon: {
    fontSize: 20,
    // color: COLOR_MIDDLE_GRAY,
    marginRight: theme.spacing(1),
  },
  factorProgress: {
    flex: 1,
    marginRight: theme.spacing(1),
  },
  factorScore: {
    width: 30,
    textAlign: 'right',
    fontSize: theme.typography.pxToRem(16),
  },
  progressBar:{
    background: COLOR_ORANGE,
  }
}), { name: 'PlayerValuations' });

const FACTOR_COLUMNS: Array<Array<{ key: string; label: string }>> = [
  [
    { key: 'ppi', label: 'PPI' },
    { key: 'ppiRec', label: 'PPI Rec.' },
    { key: 'pff', label: 'PFF' },
    { key: 'pffRec', label: 'PFF Rec.' },
    { key: 'pai', label: 'PAI' },
    { key: 'speedPercentile', label: 'Spd %' },
    { key: 'powerPercentile', label: 'Pwr %' },
    { key: 'ms', label: 'MS' },
  ],
  [
    { key: 'heightPercentile', label: 'H %' },
    { key: 'weightPercentile', label: 'W %' },
    { key: 'expLvl', label: 'Exp. Lvl' },
    { key: 'gmExp', label: 'Gm Exp.' },
    { key: '247', label: '247' },
    { key: 'portal', label: 'Portal' },
    { key: 'eligibility', label: 'Elig.' },
    { key: 'tenure', label: 'Tenure' },
  ],
  [
    { key: 'allAmerican', label: 'All-Amer.' },
    { key: 'allConference', label: 'All-Conf.' },
    { key: 'offerCount', label: 'Offer #' },
    { key: 'offerQuality', label: 'Offer Qual.' },
    { key: 'on3PublicNILValuation', label: 'On3 Val.' },
    { key: 'on3RosterValue', label: 'On3 RV' },
    { key: 'on3PrivateNILValue', label: 'On3 NV' },
    { key: 'commitStr', label: 'Commit Str.' },
    { key: 'hsAvail', label: 'HS Avail.' },
  ],
];

function buildFactorMap(factors: PlayerFactors[]): Record<string, number> {
  return factors.reduce<Record<string, number>>((acc, item) => {
    acc[item.shortName] = item.playerFactorValue;
    return acc;
  }, {});
}

function buildFactorDescriptionMap(factors: PlayerFactors[]): Record<string, string> {
  return factors.reduce<Record<string, string>>((acc, item) => {
    acc[item.shortName] = item.description || '';
    return acc;
  }, {});
}

export default function PlayerValuations(props: PlayerValuationsProps) {
  const { className, data, evaluationTeam  } = props;
  const classes = useStyles();
  const trailingValuationNames = new Set(['TF College Valuation', 'TF HS Valuation']);

  // const data = mockApiResponse; // Using mock API response
  const factorMap = buildFactorMap(data?.playerFactors || []);
  const factorDescriptionMap = buildFactorDescriptionMap(data?.playerFactors || []);
  const valuations = (data?.valuations || [])
    .filter(valuation => !!valuation?.playerEligibleForValuation)
    .map((valuation, index) => ({ valuation, index }))
    .sort((left, right) => {
      const leftIsTrailing = trailingValuationNames.has((left.valuation?.name || '').trim());
      const rightIsTrailing = trailingValuationNames.has((right.valuation?.name || '').trim());

      if (leftIsTrailing === rightIsTrailing) {
        return left.index - right.index;
      }

      return leftIsTrailing ? 1 : -1;
    })
    .map(({ valuation }) => valuation);

  return (
    <div className={clsx(classes.root, className)}>
      <div className={classes.headerBar}>
        <div className={classes.headerLeft}>
          <TFVLogoIcon className={clsx(classes.tfvIcon, classes.tfvIconHeader)} />

          <span className={classes.headerName}>Active Valuations</span>
        </div>
      </div>

      <div className={classes.summaryScroller}>
        <div className={classes.summaryRow}>
          {valuations.map((valuation, index) => {
            const isCollegeValuation = valuation?.name === 'TF College Valuation';
            const showTeamLogo = !!(evaluationTeam?.logo247 || evaluationTeam?.logoESPN) && !isCollegeValuation;

            return (
              <React.Fragment key={valuation?.id || `${valuation?.name}-${index}`}>
                <div className={clsx(classes.summaryCell, classes.summaryInfoCell)}>
                  <div className={classes.summaryBlock}>
                    <div className={classes.summaryTitle}>{valuation?.name || 'Valuation'}</div>
                    <div className={classes.summarySubtitle}>{valuation?.description || ''}</div>
                  </div>
                </div>

                <div className={clsx(classes.summaryCell, classes.summaryValueCell)}>
                  <div className={classes.summaryValue}>${formatNumber(valuation?.playerValuation || 0, 0).toLocaleString()}</div>
                </div>

                <div className={clsx(classes.summaryCell, classes.summaryScoreCell)}>
                  <div className={classes.summaryScore}>
                    <span className={classes.miniScore}>{(valuation?.playerValuationScore || 0).toFixed(1)}</span>
                    {showTeamLogo ? (
                      <img
                        src={evaluationTeam?.logo247 || evaluationTeam?.logoESPN || ''}
                        alt={evaluationTeam?.name || 'Team Logo'}
                        width={20}
                        height={20}
                        style={{ borderRadius: '50%' }}
                      />
                    ) : (
                      <TFVLogoIcon className={classes.tfvIcon} />
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className={classes.headerBar}>
        <span className={classes.headerName}>Player Factors</span>
      </div>

      <div className={classes.factorsRow}>
        <Grid container spacing={0} className={classes.factorGrid}>
          {FACTOR_COLUMNS.map((column, columnIndex) => (
            <Grid item xs={12} md={4} key={columnIndex} className={classes.factorCol}>
              {column.map((field, rowIndex) => {
                const rawValue = factorMap[field.label];
                const factorDescription = factorDescriptionMap[field.label] || '';
                const numericValue = typeof rawValue === 'number' ? rawValue : 0;
                const displayValue = rawValue === null || rawValue === undefined ? '' : rawValue;

                return (
                  <div
                    className={clsx(
                      classes.factorRow,
                      rowIndex % 2 === 0 ? classes.factorRowOdd : classes.factorRowEven,
                    )}
                    key={field.key}
                  >
                    <span className={classes.factorName}>{field.label}</span>
                    <Tooltip title={factorDescription} placement='top'>
                      <InfoIcon className={classes.factorInfoIcon} />
                    </Tooltip>
                    <div className={classes.factorProgress}>
                      <ProgressLine showPercentileSign={false} value={numericValue} progressBarClassName={classes.progressBar} />
                    </div>
                    {/* <span className={classes.factorScore}>{displayValue}</span> */}
                  </div>
                );
              })}
            </Grid>
          ))}
        </Grid>
      </div>
    </div>
  );
}

